import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Shield, 
  Zap, 
  Globe, 
  Headphones, 
  Monitor, 
  Smartphone,
  Server,
  Wifi,
  ArrowRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const features = [
  {
    icon: Shield,
    title: 'Disconnect Protection',
    description: 'Auto-reconnect keeps you live. Viewers see a holding scene, not "offline".',
  },
  {
    icon: Zap,
    title: '24/7 Streaming',
    description: 'No time limits. Stream marathons without worrying about timeouts.',
  },
  {
    icon: Globe,
    title: '36+ Server Locations',
    description: 'Global coverage with sub-100ms latency wherever you stream.',
  },
  {
    icon: Monitor,
    title: 'Remote Desktop',
    description: 'Full OBS control from anywhere. Manage overlays on the fly.',
  },
  {
    icon: Smartphone,
    title: 'IRLremote+ App',
    description: 'Switch scenes, toggle sources, and monitor stream health from your phone.',
  },
  {
    icon: Server,
    title: 'Multi-Platform',
    description: 'Broadcast to Twitch, Kick, YouTube, TikTok and more simultaneously.',
  },
  {
    icon: Wifi,
    title: 'All Protocols',
    description: 'RTMP, SRT, SRTLA, RIST — optimized relays for any encoder.',
  },
  {
    icon: Headphones,
    title: 'Expert Support',
    description: 'Real humans on Discord. IRL streaming experts ready to help.',
  },
];

export function Features() {
  return (
    <div className="min-h-screen pt-32 pb-20 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold text-white mb-4"
          >
            Everything you need
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-gray-400 text-lg"
          >
            Professional IRL streaming infrastructure, simplified.
          </motion.p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-20">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="group"
            >
              <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-6 h-full hover:border-gray-700 transition-colors">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-gray-800 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="h-5 w-5 text-cyan-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-1">
                      {feature.title}
                    </h3>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <Link to="/pricing">
            <Button
              size="lg"
              className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold px-8"
            >
              View Pricing
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
